import { forwardRef } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePlatform } from "@/hooks/usePlatform";
import { haptics } from "@/utils/haptics";

interface NativeButtonProps extends ButtonProps {
  /** Trigger a light haptic on press (mobile only). Default: true. */
  haptic?: boolean;
}

/**
 * NativeButton — Button with platform-tuned press behaviour:
 *  • iOS / iPad     → 0.96 scale + spring-feel CSS transition + light haptic
 *  • Android        → M3 ripple-ish opacity dip + elevation lift + haptic
 *  • Desktop        → standard hover/active states
 *
 * Always meets the 44pt iOS / 48dp Android tap-target minimum.
 */
export const NativeButton = forwardRef<HTMLButtonElement, NativeButtonProps>(
  ({ className, onClick, haptic = true, ...props }, ref) => {
    const { isIOS, isIPad, isAndroidPhone, isAndroidTablet, isMobile } = usePlatform();
    const isApple = isIOS || isIPad;
    const isAndroid = isAndroidPhone || isAndroidTablet;

    return (
      <Button
        ref={ref}
        {...props}
        onClick={(e) => {
          if (haptic && isMobile) haptics.light();
          onClick?.(e);
        }}
        className={cn(
          "min-h-[44px] touch-manipulation",
          isApple &&
            "transition-transform duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)] active:scale-[0.96]",
          isAndroid &&
            "transition-all duration-200 ease-[cubic-bezier(0.05,0.7,0.1,1)] active:opacity-80 active:elev-2",
          className,
        )}
      />
    );
  },
);
NativeButton.displayName = "NativeButton";

export default NativeButton;
